import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import Integration from "@/model/interface/Integration";
import IntegrationSyncToggle from "@/components/pages/integrations/IntegrationSyncToggle";
import { useConnectInvestorFuseMutation, useUpdateIntegrationSettingsMutation } from "@/store/rest_api";
import toast from "react-hot-toast";

interface Props {
  integration: Integration;
}

export default function InvestorFuseSettings({ integration }: Props) {
  const brand = integration.service === "carrot" ? "Carrot" : "InvestorFuse";
  const settings = integration.settings || {};
  const [connect, { isLoading: isConnecting }] = useConnectInvestorFuseMutation();
  const [updateSettings] = useUpdateIntegrationSettingsMutation();

  const [apiKey, setApiKey] = useState("");
  const [webhookKey, setWebhookKey] = useState((settings.webhookKey as string) || "");
  const [teamId, setTeamId] = useState((settings.teamId as string) || "");

  const handleConnect = async () => {
    if (!apiKey.trim()) {
      toast.error("API key is required");
      return;
    }
    try {
      await connect({ integrationId: integration.id, apiKey, webhookKey, teamId }).unwrap();
      toast.success(`${brand} connected`);
      setApiKey("");
    } catch {
      toast.error("Failed to connect");
    }
  };

  const toggleSetting = async (key: string, value: boolean) => {
    try {
      await updateSettings({ id: integration.id, settings: { [key]: value } }).unwrap();
    } catch {
      toast.error("Failed to update setting");
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Connection</CardTitle>
          <CardDescription>
            Connect with your {brand} API key, webhook key and team ID. Activity (calls and SMS) is pushed to {brand}.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">Status</p>
            <Badge variant={integration.status === "active" ? "default" : "secondary"}>{integration.status}</Badge>
          </div>

          <IntegrationSyncToggle integration={integration} />

          <div className="space-y-2">
            <Label htmlFor="apiKey">API Key</Label>
            <Input
              id="apiKey"
              type="password"
              placeholder={integration.status === "active" ? "Enter a new key to replace the existing one" : "API key"}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="webhookKey">Webhook Key</Label>
            <Input
              id="webhookKey"
              placeholder="Webhook key"
              value={webhookKey}
              onChange={(e) => setWebhookKey(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="teamId">Team ID</Label>
            <Input id="teamId" placeholder="Team ID" value={teamId} onChange={(e) => setTeamId(e.target.value)} />
          </div>

          <Button onClick={handleConnect} disabled={isConnecting}>
            {isConnecting ? "Connecting..." : integration.status === "active" ? "Update Connection" : "Connect"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Auto Lead Creation</CardTitle>
          <CardDescription>Create a {brand} lead for calls and texts from unknown contacts</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <Label htmlFor="autoCreateLeads" className="flex flex-col gap-1">
              <span>Auto-create leads</span>
              <span className="font-normal text-muted-foreground text-xs">
                When off, activity is only logged for contacts already linked to {brand}
              </span>
            </Label>
            <Switch
              id="autoCreateLeads"
              checked={!!settings.autoCreateLeads}
              onCheckedChange={(value) => toggleSetting("autoCreateLeads", value)}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
